import { Spinner, Typography } from '@weareopenr/component-library';
import { getPublishTime } from './getPublishTime/getPublishTime';
import CollapsibleSection from 'src/components/CollapsibleSection';
import { getMealtimesSubtitle } from './getMealtimesSubtitle/getMealtimesSubtitle';
import { groupAndFormatSchedules } from 'src/pages/Mealtimes/groupAndFormatSchedules/groupAndFormatSchedules';
import { ISummaryViewProps } from './types';
import { IMealtime } from 'src/types/mealtimes.types';
import styles from './SummaryView.module.scss';
import { usePriceScheme } from 'src/hooks/usePriceScheme';
const SummaryView = ({
  menuName,
  sections,
  watch,
  publicationData,
  filteredMealtimes,
  isMealtimeOptionEnabled,
}: ISummaryViewProps): React.ReactElement => {
  const { data: priceScheme, isLoading: isLoadingPriceScheme } = usePriceScheme(watch('price-scheme'));
  if (isLoadingPriceScheme) {
    return (
      <section className={styles['loading-container']}>
        <Spinner size="xlarge" colour="rainbow" />
      </section>
    );
  }

  const channels = watch('channels');
  const activePublicationVenues = publicationData.map((channel) => ({
    ...channel,
    venues: channel.venues.filter(({ venue_id }) => channels?.[channel.channel_id]?.[venue_id]),
  }));

  const numberOfVenues = activePublicationVenues.reduce(
    (acc, publishDestination) => acc + publishDestination.venues.length,
    0,
  );
  const formValues = watch();

  return (
    <section>
      <Typography size="xxlarge" className={styles['heading']}>
        Summary for <strong>{menuName}</strong>
      </Typography>
      <div className={styles['sections']}>
        <CollapsibleSection
          number="1"
          title="Mealtimes"
          subtitle={getMealtimesSubtitle(filteredMealtimes, sections, isMealtimeOptionEnabled)}
        >
          {isMealtimeOptionEnabled && !!filteredMealtimes.length && (
            <div className={styles['schedules']}>
              {filteredMealtimes.map((mealtime: IMealtime) => {
                const schedules = groupAndFormatSchedules(mealtime.schedules);
                return (
                  <div key={mealtime.name}>
                    <table>
                      <thead>
                        <tr>
                          <th className={styles['mealtime-name']}>
                            <Typography weight="semi-bold" transform="capitalize">
                              {mealtime.name}:
                            </Typography>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {schedules.map((schedule) => (
                          <tr key={`${schedule.startDay}-${schedule.endDay}-${schedule.time}`}>
                            <td className={styles['day']}>
                              <Typography>
                                {`${schedule.startDay}${schedule.endDay ? ' - ' + schedule.endDay : ''}:`}
                              </Typography>
                            </td>
                            <td>
                              <Typography>{schedule.time}</Typography>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
          )}
        </CollapsibleSection>
        <CollapsibleSection number="2" title="Price Scheme" subtitle={priceScheme.name} />
        <CollapsibleSection
          number="3"
          title="Publish destination"
          subtitle={`${numberOfVenues} ${numberOfVenues > 1 ? 'venues' : 'venue'} selected`}
        >
          <div className={styles['channel-container']}>
            {activePublicationVenues.map(
              (publishDestination) =>
                publishDestination.venues.length > 0 && (
                  <div key={publishDestination.name} className={styles['channel']}>
                    <Typography weight="semi-bold">{publishDestination.name}</Typography>
                    <div className={styles['venue-container']}>
                      {publishDestination.venues.map((venue) => (
                        <div
                          key={`${publishDestination.name} ${venue.name}`}
                          data-testid={`${publishDestination.name} ${venue.name}`}
                        >
                          <Typography>{venue.name}</Typography>
                        </div>
                      ))}
                    </div>
                  </div>
                ),
            )}
          </div>
        </CollapsibleSection>
        <CollapsibleSection
          number="4"
          title="Published date & time"
          subtitle={getPublishTime(formValues.publicationDate, formValues.publicationType)}
        />
      </div>
    </section>
  );
};

export default SummaryView;
